import React, { useCallback, useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import { toast } from "react-toastify";

import { NURSE_COLORS } from "../../../nurse-dashboard/theme";
import { getCensusBeds } from "../../api/bedCensusApi";
import {
  getActiveBedRetentions,
  getBedRetentionHistory,
  releaseBedRetention,
} from "../../api/bedRetentionApi";
import RetainBedModal from "./RetainBedModal";
import { formatIstDateTime } from "../../../../utils/istDateTime";

function fmt(dt) {
  return formatIstDateTime(dt, "-");
}

export default function BedRetentionManagement() {
  const [active, setActive] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [releasingCode, setReleasingCode] = useState(null);

  // Bed picker -> RetainBedModal. Only "available" beds can be retained
  // (enforced by manage_bed_retention.py), so that's all we offer.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [availableBeds, setAvailableBeds] = useState([]);
  const [bedsLoading, setBedsLoading] = useState(false);
  const [pickedBed, setPickedBed] = useState(null);
  const [retainCode, setRetainCode] = useState(null);

  const openPicker = async () => {
    setPickerOpen(true);
    setPickedBed(null);
    setBedsLoading(true);
    try {
      const rows = await getCensusBeds({ status: "available" });
      setAvailableBeds(rows || []);
    } catch (err) {
      console.error("Failed to load available beds:", err);
      toast.error("Failed to load available beds.");
    } finally {
      setBedsLoading(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [activeRows, historyRows] = await Promise.all([
        getActiveBedRetentions(),
        getBedRetentionHistory(),
      ]);
      setActive(activeRows);
      setHistory(historyRows);
    } catch (err) {
      console.error("Failed to load bed retentions:", err);
      toast.error("Failed to load bed retentions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRelease = async (bedCode) => {
    setReleasingCode(bedCode);
    try {
      const data = await releaseBedRetention(bedCode);
      if (data?.status === "Success") {
        toast.success(data.message || `Bed ${bedCode} released.`);
        load();
      } else {
        toast.error(data?.message || "Could not release the retention.");
      }
    } catch (err) {
      console.error("Release bed retention failed:", err);
      toast.error(err?.response?.data?.message || "Could not release the retention.");
    } finally {
      setReleasingCode(null);
    }
  };

  return (
    <Box sx={{ bgcolor: NURSE_COLORS.pageBg, minHeight: "100vh", p: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Typography variant="h6" color={NURSE_COLORS.textPrimary}>
          Bed Retention
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            startIcon={<EventBusyIcon />}
            variant="contained"
            onClick={openPicker}
            sx={{ bgcolor: NURSE_COLORS.primary, borderRadius: 20, textTransform: "none", "&:hover": { bgcolor: NURSE_COLORS.primaryDark } }}
          >
            Retain a Bed
          </Button>
          <Button
            startIcon={<RefreshIcon />}
            onClick={load}
            disabled={loading}
            sx={{ color: NURSE_COLORS.primary, border: `1px solid ${NURSE_COLORS.border}`, borderRadius: 20, textTransform: "none" }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}`, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          Active Retentions
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell>Bed</TableCell>
              <TableCell>Retained By</TableCell>
              <TableCell>Retained At</TableCell>
              <TableCell>Expires At</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {active.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: NURSE_COLORS.textSecondary }}>
                  No active retentions
                </TableCell>
              </TableRow>
            )}
            {active.map((r) => (
              <TableRow key={r.id}>
                <TableCell>{r.bed_code}</TableCell>
                <TableCell>{r.retained_by_name || r.retained_by || "-"}</TableCell>
                <TableCell>{fmt(r.retained_at)}</TableCell>
                {/* Expired retentions auto-release server-side (nursing-ops-2
                    #10), so an active row is never past its expiry. */}
                <TableCell>{fmt(r.expires_at)}</TableCell>
                <TableCell>{r.reason || "-"}</TableCell>
                <TableCell align="right">
                  <Button
                    size="small"
                    color="error"
                    variant="outlined"
                    disabled={releasingCode === r.bed_code}
                    onClick={() => handleRelease(r.bed_code)}
                  >
                    {releasingCode === r.bed_code ? "Releasing..." : "Release"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
        <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
          History
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: NURSE_COLORS.pageBg }}>
              <TableCell>Bed</TableCell>
              <TableCell>Action</TableCell>
              <TableCell>Performed By</TableCell>
              <TableCell>Performed At</TableCell>
              <TableCell>Reason</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {history.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ color: NURSE_COLORS.textSecondary }}>
                  No history yet
                </TableCell>
              </TableRow>
            )}
            {history.map((h) => (
              <TableRow key={h.id}>
                <TableCell>{h.bed_code}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={h.action}
                    color={h.action === "retained" ? "warning" : "success"}
                  />
                </TableCell>
                <TableCell>{h.performed_by_name || h.performed_by || "-"}</TableCell>
                <TableCell>{fmt(h.performed_at)}</TableCell>
                <TableCell>{h.reason || "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Retain a Bed</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "14px", mb: 1.5 }}>
            Pick an available bed to reserve.
          </Typography>
          <Autocomplete
            options={availableBeds}
            loading={bedsLoading}
            value={pickedBed}
            onChange={(_e, val) => setPickedBed(val)}
            getOptionLabel={(b) => (b?.location ? `${b.code} · ${b.location}` : b?.code || "")}
            isOptionEqualToValue={(a, b) => a?.code === b?.code}
            noOptionsText={bedsLoading ? "Loading..." : "No available beds"}
            renderInput={(params) => (
              <TextField {...params} size="small" label="Available bed" autoFocus />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!pickedBed}
            onClick={() => {
              setRetainCode(pickedBed.code);
              setPickerOpen(false);
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      <RetainBedModal
        open={Boolean(retainCode)}
        bedCode={retainCode}
        onClose={() => setRetainCode(null)}
        onRetained={() => {
          setRetainCode(null);
          load();
        }}
      />
    </Box>
  );
}
